import { useEffect, useState } from 'react'
import { Flex, Text, VStack, Box, Stack, Image, Button, useColorModeValue, HStack, IconButton } from '@chakra-ui/react'
import { useRecoilValue } from 'recoil'
import { circlesBreakpointState } from './state'
import { BITCOIN_ORANGE, menuLabelFontSize } from './constants'
import { getBreakpointValue } from './utils'
import { AppleLogo, GooglePlayLogo } from './components/svg'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'

const NativeApp = () => {
	const circlesBreakpoint = useRecoilValue(circlesBreakpointState)
	const [currentSlide, setCurrentSlide] = useState(0)
	
	// Store URLs
	const appStoreUrl = 'https://apps.apple.com/app/timechain-calendar/id6749210824'
	const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.timechaincalendar.tcpro'
	
	const interactionStyle = {
		borderColor: BITCOIN_ORANGE,
		color: BITCOIN_ORANGE,
	}
	const responsiveButtonLabelSize = getBreakpointValue(menuLabelFontSize, circlesBreakpoint)
	const buttonLineHeight = getBreakpointValue({ base: '26px', md: '36px' }, circlesBreakpoint)
	const bg = useColorModeValue('white', 'black')
	const color = useColorModeValue('black', 'white')

	// Slide data with text content for each image
	const slides = [
		{
			image: '/app/slide0.jpg',
			croppedImage: '/app/slide0-crop.jpg',
			title: 'You can just watch the timechain.',
			color: 'white'
		},
		{
			image: '/app/slide1.jpg',
			croppedImage: '/app/slide1-crop.jpg',
			title: 'Monitor the chain',
			color: 'white'
		},
		{
			image: '/app/slide2.jpg',
			croppedImage: '/app/slide2-crop.jpg',
			title: 'Explore the past',
			color: BITCOIN_ORANGE
		},
		{
			image: '/app/slide3.jpg',
			croppedImage: '/app/slide3-crop.jpg',
			title: 'Estimate the future',
			color: 'green100'
		},
		{
			image: '/app/slide4.jpg',
			croppedImage: '/app/slide4-crop.jpg',
			title: 'Block-stamp & Price-stamp your moments',
			color: 'white'
		},
		{
			image: '/app/slide5.jpg',
			croppedImage: '/app/slide5-crop.jpg',
			title: 'Stay notified',
			color: 'red100'
		},
		{
			image: '/app/slide6.jpg',
			croppedImage: '/app/slide6-crop.jpg',
			title: 'Surface the bitcoin data you care about most',
			color: 'white'
		}
	]

	const handlePrevSlide = () => {
		setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1))
	}

	const handleNextSlide = () => {
		setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1))
	}

	const handleDotClick = (index: number) => {
		setCurrentSlide(index)
	}

	useEffect(() => {
		// Auto-advance slideshow every 5 seconds
		const interval = setInterval(() => {
			handleNextSlide()
		}, 5000)

		return () => clearInterval(interval)
	}, [currentSlide])

	return (
		<Flex
			w="100%"
			direction="column"
			align="center"
			justify="flex-start"
			p={4}
			pt={4}
			pb={8}
		>
			{/* Header */}
			<VStack spacing={2} mb={8}>
				<Text
					fontSize={{ base: '12px', md: '14px' }}
					fontWeight="normal"
					letterSpacing="0.15em"
					textTransform="uppercase"
					color="gray.400"
				>
					Introducing
				</Text>
				
				<Text
					fontSize={{ base: '24px', sm: '30px', md: '36px' }}
					fontWeight="bold"
					letterSpacing="-0.02em"
					textAlign="center"
					lineHeight="1"
				>
					TIMECHAIN CALENDAR
				</Text>
				
				<Text
					fontSize={{ base: '14px', md: '16px' }}
					fontWeight="normal"
					letterSpacing="0.1em"
					textTransform="uppercase"
					color="gray.400"
				>
					For Mobile
				</Text>
			</VStack>
		
			{/* Slideshow Section */}
			<VStack spacing={0} maxW="900px" w="100%">
				{/* Slideshow Container with overlapping title */}
				<Box position="relative" w="100%" maxW="600px" mx="auto">
					{/* Slide Title - Overlapping */}
					<Text
						fontSize={{ base: '16px', sm: '18px', md: '24px' }}
						fontWeight="bold"
						textAlign="center"
						color={slides[currentSlide].color}
						position="absolute"
						top={{ base: '5px', md: '10px' }}
						left="50%"
						transform="translateX(-50%)"
						zIndex={20}
						textShadow="0 2px 4px rgba(0,0,0,0.8)"
						lineHeight="1.1"
						maxW="90%"
					>
						{slides[currentSlide].title}
					</Text>
		
					{/* Navigation Buttons */}
					<IconButton
						aria-label="Previous slide"
						icon={<ChevronLeftIcon boxSize={8} />}
						position="absolute"
						left={{ base: '5px', md: '-5px' }}
						top="50%"
						transform="translateY(-50%)"
						bg="blackAlpha.600"
						color="white"
						_hover={{ bg: 'blackAlpha.800', color: BITCOIN_ORANGE }}
						_active={{ bg: 'blackAlpha.800', color: BITCOIN_ORANGE }}
						onClick={handlePrevSlide}
						zIndex={30}
						size="lg"
						borderRadius="full"
					/>
					
					<IconButton
						aria-label="Next slide"
						icon={<ChevronRightIcon boxSize={8} />}
						position="absolute"
						right={{ base: '5px', md: '-5px' }}
						top="50%"
						transform="translateY(-50%)"
						bg="blackAlpha.600"
						color="white"
						_hover={{ bg: 'blackAlpha.800', color: BITCOIN_ORANGE }}
						_active={{ bg: 'blackAlpha.800', color: BITCOIN_ORANGE }}
						onClick={handleNextSlide}
						zIndex={30}
						size="lg"
						borderRadius="full"
					/>
		
					{/* Slide Image */}
					<Box
						w="100%"
						aspectRatio={{ base: 600/1024, sm: 1 }}
						overflow="hidden"
						borderRadius="lg"
						bg="gray.900"
						position="relative"
					>
						{slides.map((slide, index) => (
							<Image
								key={index}
								src={circlesBreakpoint === 'base' ? slide.croppedImage : slide.image}
								alt={slide.title}
								w="100%"
								h="100%"
								objectFit="cover"
								position="absolute"
								top={0}
								left={0}
								opacity={index === currentSlide ? 1 : 0}
								transition="opacity 0.8s ease-in-out"
							/>
						))}
					</Box>
					
					{/* Dots Indicator - Overlaying bottom of slideshow */}
					<HStack 
						spacing={2} 
						position="absolute"
						bottom="20px"
						left="50%"
						transform="translateX(-50%)"
						zIndex={30}
					>
						{slides.map((_, index) => (
							<Box
								key={index}
								w="8px"
								h="8px"
								borderRadius="full"
								bg={index === currentSlide ? BITCOIN_ORANGE : 'gray.400'}
								cursor="pointer"
								transition="all 0.3s ease"
								_hover={{ bg: index === currentSlide ? BITCOIN_ORANGE : 'gray.200' }}
								onClick={() => handleDotClick(index)}
								boxShadow="0 2px 4px rgba(0,0,0,0.8)"
							/>
						))}
					</HStack>
				</Box>
			</VStack>
		
			{/* App Store Buttons */}
			<Stack direction={{ base: 'column', sm: 'row' }} spacing={{ base: 4, sm: 8 }} mt={8}>
				{/* iOS Download Button */}
				<VStack spacing={2}>
					<Box
						as="a"
						href={appStoreUrl}
						target="_blank"
						rel="noopener noreferrer"
						cursor="pointer"
						role="group"
					>
						<Button
							w={{ base: '140px', md: '160px' }}
							h={{ base: '50px', md: '55px' }}
							size="md"
							variant="outline"
							bg={bg}
							fontSize={responsiveButtonLabelSize}
							lineHeight={buttonLineHeight}
							py="4px"
							px="12px"
							color={color}
							borderColor={color}
							borderWidth={2}
							borderRadius="md"
							_active={interactionStyle}
							_hover={interactionStyle}
							_groupHover={interactionStyle}
							display="flex"
							alignItems="center"
							justifyContent="center"
							gap={2}
						>
							<AppleLogo size={24} />
							<Text>App Store</Text>
						</Button>
					</Box>
				</VStack>
				
				{/* Android Download Button */}
				<VStack spacing={2}>
					<Box
						as="a"
						href={playStoreUrl}
						target="_blank"
						rel="noopener noreferrer"
						cursor="pointer"
						role="group"
					>
						<Button
							w={{ base: '140px', md: '160px' }}
							h={{ base: '50px', md: '55px' }}
							size="md"
							variant="outline"
							bg={bg}
							fontSize={responsiveButtonLabelSize}
							lineHeight={buttonLineHeight}
							py="4px"
							px="12px"
							color={color}
							borderColor={color}
							borderWidth={2}
							borderRadius="md"
							_active={interactionStyle}
							_hover={interactionStyle}
							_groupHover={interactionStyle}
							display="flex"
							alignItems="center"
							justifyContent="center"
							gap={2}
						>
							<GooglePlayLogo size={24} />
							<Text>Google Play</Text>
						</Button>
					</Box>
				</VStack>
			</Stack>
		
			{/* Description Section */}
			<VStack
				spacing={6}
				mt={12}
				maxW="700px"
				w="100%"
				align="center"
				px={4}
			>
				<Text
					fontSize="16px"
					lineHeight="1.6"
					color="gray.300"
					textAlign="center"
				>
					TIMECHAIN CALENDAR is a Bitcoin protocol visualizer, timechain explorer, and live data dashboard. This compact interface provides a comprehensive view at-a-glance of the Bitcoin network&apos;s most important features. Search and explore the entire chain to find past blocks or estimate future blocks by height or date. Stay informed and connected!
				</Text>
		
				<Text
					fontSize="16px"
					lineHeight="1.6"
					color="gray.300"
					textAlign="center"
				>
					Subscribers unlock native mobile tools to enhance the experience. Block-stamp and price-stamp your special moments with the Timechain Calendar camera. Stay notified with a suite of alerts about the info that matters to you most. Install widgets that surface the latest data into your view without needing to open the app.
				</Text>

				<VStack spacing={3} align="center" w="100%" maxW="500px">
					<Text
						fontSize="18px"
						fontWeight="bold"
						color="white"
						textAlign="center"
						w="100%"
					>
						Features:
					</Text>
					<VStack spacing={2} align="center" w="100%">
						<Text fontSize="14px" color="gray.300" textAlign="center">• Block production monitoring</Text>
						<Text fontSize="14px" color="gray.300" textAlign="center">• Complete timechain block search</Text>
						<Text fontSize="14px" color="gray.300" textAlign="center">• Future block estimation by height or date</Text>
						<Text fontSize="14px" color="gray.300" textAlign="center">• Camera with live block and price data overlays</Text>
						<Text fontSize="14px" color="gray.300" textAlign="center">• Customizable price, block, mining pool and calendar alerts</Text>
						<Text fontSize="14px" color="gray.300" textAlign="center">• Beautiful home screen widgets</Text>
					</VStack>
				</VStack>
			</VStack>
			
			{/* Bottom Spacer */}
			<Box h="100px" />
		</Flex>
	)
}

export default NativeApp